/**
 * FullStory utility functions for debugging and verification
 */

declare global {
  interface Window {
    FS?: {
      event: (eventName: string, properties?: Record<string, unknown>) => void;
      identify: (uid: string, userVars?: Record<string, unknown>) => void;
      setUserVars: (userVars: Record<string, unknown>) => void;
      getCurrentSessionURL: (now?: boolean) => string | null;
      // Add other FullStory methods as needed
    };
  }
}

import { FullStory, isInitialized } from '@fullstory/browser';

function getGlobalScope(): Window {
  return globalThis as Window;
}

/**
 * Whether the @fullstory/browser SDK has been initialized (via InitializeFullStory).
 * Prefer this over isFullStoryAvailable() for sending custom events.
 */
export function isFullStorySdkInitialized(): boolean {
  try {
    return isInitialized();
  } catch (error) {
    console.error('[FullStory] Error checking SDK initialization:', error);
    return false;
  }
}

/**
 * Check if the legacy window.FS object is present (diagnostics / test helpers).
 * @returns true if window.FS is available, false otherwise
 */
export function isFullStoryAvailable(): boolean {
  try {
    const globalScope = getGlobalScope();

    // Check if the FullStory object is available on the global scope
    // Note: typeof null === 'object', so we need to explicitly check for null
    const hasFS =
      'FS' in globalScope &&
      typeof globalScope.FS === 'object' &&
      globalScope.FS !== null;

    return hasFS;
  } catch (error) {
    console.error('[FullStory] Error checking availability:', error);
    return false;
  }
}

/**
 * Get the current FullStory session URL for debugging
 * @returns The session URL or null if not available
 */
export function getFullStorySessionUrl(): string | null {
  try {
    const fs = getGlobalScope().FS;
    if (!isFullStoryAvailable() || !fs?.getCurrentSessionURL) {
      return null;
    }
    return fs.getCurrentSessionURL(true);
  } catch (error) {
    console.error('[FullStory] Error getting session URL:', error);
    return null;
  }
}

/**
 * Verify FullStory is working by sending a test event
 * @param testEventName - Optional custom test event name
 * @returns true if test event was sent successfully
 */
/**
 * Sets analytics user variables in FullStory when available.
 */
export function setFullStoryAnalyticsUserVars(userVars: {
  userID?: string;
  uuid?: string;
  userUUID?: string;
}): void {
  const vars: Record<string, string> = {};

  if (userVars.userID) {
    vars.userID = userVars.userID;
  }

  if (userVars.uuid) {
    vars.uuid = userVars.uuid;
  }

  if (userVars.userUUID) {
    vars.userUUID = userVars.userUUID;
  }

  if (Object.keys(vars).length === 0) {
    return;
  }

  try {
    if (!isFullStorySdkInitialized()) {
      return;
    }

    FullStory.setUserVars(vars);
  } catch (error) {
    console.error('[FullStory] Error setting analytics user vars:', error);
  }
}

/**
 * Sets the userID user variable in FullStory when available.
 */
export function setFullStoryEmailId(email: string | undefined): void {
  setFullStoryAnalyticsUserVars({ userID: email });
}

/**
 * Logs a custom event to FullStory with userID preserved in properties.
 * core-utils strips top-level userID from FullStory trackEvent payloads.
 */
export function logFullStoryUserEvent(properties: Record<string, unknown>): void {
  if (!isFullStorySdkInitialized()) {
    console.warn(
      '[FullStory] SDK not initialized; skipping event:',
      properties.action ?? properties.type,
    );
    return;
  }

  try {
    const { action: name, ...rest } = properties;
    FullStory('trackEvent', {
      name: String(name ?? properties.type ?? 'unknown'),
      properties: rest,
    });
  } catch (error) {
    console.error('[FullStory] Error logging user event:', error);
  }
}

export function testFullStoryEvent(testEventName: string = 'FullStoryTest'): boolean {
  try {
    const fs = getGlobalScope().FS;
    if (!isFullStoryAvailable() || !fs?.event) {
      console.warn('[FullStory] Cannot send test event - FullStory not available');
      return false;
    }

    fs.event(testEventName, {
      timestamp: new Date().toISOString(),
      test: true,
    });
    
    console.log(`[FullStory] Test event "${testEventName}" sent successfully`);
    return true;
  } catch (error) {
    console.error('[FullStory] Error sending test event:', error);
    return false;
  }
}
